import { FC, useState, useCallback, useMemo } from 'react';
import { Creator } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/task.pb';
import isEqual from 'lodash-es/isEqual';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { DEFAULT_FILTER_VALUES, SelectedFilters } from '@frontend/task-center-scope';
import { Button, PopoverDialog, usePopoverDialog, Dot } from '@frontend/design-system';
import { useGlobalFilterState } from '../../hooks/use-global-filter-state';
import { taskCenterTrackingIds, contactPanelTaskListTrackingIds } from '../../tracking-ids';
import { filtersIndicatorStyle, filtersContainerStyle } from './common-styles';
import { popoverStyle, applyButtonStyle } from './task-filters.styles';
import { TaskPersonFilter } from './task-person-filter';
import { TaskPopoverHeading } from './task-popover-heading';
import { TaskSortOrder } from './task-sort-order';
import { TaskSourceFilter } from './task-source-filter';

interface Props {
  fromContactPanel?: boolean;
}

export const TaskListFilters: FC<Props> = ({ fromContactPanel = false }) => {
  const { selectedFilters, setFilters: setStoreFilters } = useGlobalFilterState(fromContactPanel);
  const [filters, setFilters] = useState<SelectedFilters>({ ...selectedFilters });
  const { t } = useTranslation('task-center');
  const popoverDialogProps = usePopoverDialog({ placement: 'bottom-start' });

  const trackingIds = fromContactPanel ? contactPanelTaskListTrackingIds.filters : taskCenterTrackingIds.filters;

  const disableFiltersClear = !selectedFilters.creator && !filters.source;

  const globalFilterApplied = useMemo(() => {
    const { creator, sortType, source } = selectedFilters;
    return creator || sortType || source;
  }, [selectedFilters]);

  // Filters Clear functions
  const onClearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTER_VALUES, sortType: filters.sortType });
  }, [setFilters, filters.sortType]);
  const onClearSort = useCallback(() => {
    setFilters({ ...filters, sortType: DEFAULT_FILTER_VALUES.sortType });
  }, [setFilters, filters]);

  // Filters data update functions
  const setCreator = useCallback((creator: Creator) => setFilters({ ...filters, creator }), [filters]);
  const setSource = useCallback((source: TaskCenterTypes.TaskSource) => setFilters({ ...filters, source }), [filters]);
  const setSortOrder = useCallback(
    (sortType: TaskCenterTypes.SortField | null) => setFilters({ ...filters, sortType }),
    [filters]
  );

  // Apply button functions
  const disableApply = isEqual(selectedFilters, filters);
  const onClickApply = useCallback(() => {
    setStoreFilters(filters);
    popoverDialogProps.close();
  }, [filters, setStoreFilters]);

  return (
    <div css={filtersContainerStyle}>
      <Button
        {...popoverDialogProps.getTriggerProps()}
        variant='secondary'
        iconName='filter-alt'
        size='medium'
        hoverLabel={t('Filters')}
        trackingId={trackingIds.otherFilters}
      />
      {globalFilterApplied && <Dot css={filtersIndicatorStyle} color='primary' />}
      <PopoverDialog {...popoverDialogProps.getDialogProps()} css={popoverStyle}>
        <TaskPopoverHeading
          label={t('Sort Order')}
          onClear={onClearSort}
          disableClear={!filters.sortType}
          trackingId={trackingIds.clearSortFiltersButton}
        />
        <TaskSortOrder
          sortOrder={filters.sortType}
          setSortOrder={setSortOrder}
          priorityDescTrackingId={trackingIds.priorityDescendingFilter}
          dueDateTrackingId={trackingIds.dueDateFilter}
        />
        <TaskPopoverHeading
          label={t('Other Filters')}
          onClear={onClearFilters}
          disableClear={disableFiltersClear}
          trackingId={trackingIds.clearOtherFiltersButton}
        />
        <TaskPersonFilter
          selectedValue={filters.creator}
          setValue={setCreator}
          label={t('Created By')}
          trackingId={trackingIds.createdByFilter}
        />
        <TaskSourceFilter source={filters.source} setSource={setSource} trackingIds={trackingIds} />
        <Button
          onClick={onClickApply}
          disabled={disableApply}
          css={applyButtonStyle}
          trackingId={trackingIds.applyOtherFiltersButton}
        >
          {t('Apply')}
        </Button>
      </PopoverDialog>
    </div>
  );
};
